import { useEffect } from 'react';

const SITE_URL = 'https://nuisibles-guadeloupe.fr';
const OG_IMAGE = SITE_URL + '/og-image.png';

interface DocumentHeadProps {
  title: string;
  description: string;
  noindex?: boolean;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  /** Un ou plusieurs objets JSON-LD (schema.org) à injecter dans le <head>. */
  jsonLd?: object | object[];
}

export function useDocumentHead({
  title,
  description,
  noindex = false,
  ogTitle,
  ogDescription,
  ogType = 'website',
  ogUrl,
  jsonLd,
}: DocumentHeadProps) {
  useEffect(() => {
    document.title = title;

    const setOrCreateMeta = (attr: string, value: string, propName?: string) => {
      const selector = propName
        ? `meta[${propName}="${attr}"]`
        : `meta[name="${attr}"]`;
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        if (propName) {
          el.setAttribute(propName, attr);
        } else {
          el.setAttribute('name', attr);
        }
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
    };

    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    const absoluteUrl = ogUrl || (SITE_URL + (path === '/' ? '/' : path));

    // Canonical
    let linkCanon = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!linkCanon) { linkCanon = document.createElement('link'); linkCanon.setAttribute('rel', 'canonical'); document.head.appendChild(linkCanon); }
    linkCanon.setAttribute('href', absoluteUrl);

    // Description
    setOrCreateMeta('description', description);

    // Robots
    if (noindex) {
      setOrCreateMeta('robots', 'noindex, nofollow');
    } else {
      setOrCreateMeta('robots', 'index, follow, max-image-preview:large');
    }

    // Open Graph
    setOrCreateMeta('og:site_name', 'Gwada Nuisibles', 'property');
    setOrCreateMeta('og:title', ogTitle || title, 'property');
    setOrCreateMeta('og:description', ogDescription || description, 'property');
    setOrCreateMeta('og:type', ogType, 'property');
    setOrCreateMeta('og:url', absoluteUrl, 'property');
    setOrCreateMeta('og:locale', 'fr_FR', 'property');
    setOrCreateMeta('og:image', OG_IMAGE, 'property');

    // Twitter Card
    setOrCreateMeta('twitter:card', 'summary_large_image');
    setOrCreateMeta('twitter:title', ogTitle || title);
    setOrCreateMeta('twitter:description', ogDescription || description);
    setOrCreateMeta('twitter:image', OG_IMAGE);

    // JSON-LD (données structurées) — nettoie les précédentes puis (ré)injecte
    document.querySelectorAll('script[data-seo-jsonld="1"]').forEach((n) => n.remove());
    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      blocks.forEach((block) => {
        const s = document.createElement('script');
        s.type = 'application/ld+json';
        s.setAttribute('data-seo-jsonld', '1');
        s.textContent = JSON.stringify(block);
        document.head.appendChild(s);
      });
    }
  }, [title, description, noindex, ogTitle, ogDescription, ogType, ogUrl, jsonLd]);
}
